import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useMe } from "@/api/queries";
import type { Role } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { homePath, reaches } from "@/auth/roles";
import type { Reach } from "@/auth/roles";
import { Loading } from "@/ui";

/** Everything below needs a signed-in person. Others are sent to the sign-in page. */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <Loading fullscreen />;
  // The query string goes along, so /connect?code=… survives the round trip through sign-in.
  if (status === "signed-out") return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

/**
 * Show the page only to the listed roles, and to anyone whose job role reaches it (`reach`, Who sees whom); anyone
 * else goes to their own home. This is for navigation only. The API enforces the same rule on every request.
 */
export function RequireRole({ roles, reach, children }: { roles: readonly Role[]; reach?: Reach; children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null;
  if (roles.includes(user.role)) return <>{children}</>;
  return reach ? <RequireReach reach={reach} role={user.role} home={homePath(user.role)}>{children}</RequireReach> : <Navigate to={homePath(user.role)} replace />;
}

/** The page for someone whose access (read from /v1/me, which may still be on its way) reaches it. */
function RequireReach({ reach, role, home, children }: { reach: Reach; role: Role; home: string; children: ReactNode }) {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (!reaches(reach, me.data?.access, role)) return <Navigate to={home} replace />;
  return <>{children}</>;
}
